/* ============================================================
   Store entry point — mounts the data layer and re-exports the shared contract
   + hook. Screens import `useStore` and never learn how it's wired.

   ApiStoreProvider is the single data layer: real Cognito auth + the deployed
   API. It keeps AppState locally for instant optimistic updates, applies the
   pure transforms/selectors in store/logic.ts, mirrors each change to the
   backend, and hydrates from a full sync on mount.

   (A dev/test harness was previously provided by a separate in-memory mock
   store; when one is reintroduced it should mock at the HTTP seam — api/client
   — so this single store path stays the only one, no divergent logic.)
   ============================================================ */
import React from 'react';
import { ApiStoreProvider } from './api';
import { WriteGuard } from './write-guard';

export { useStore } from './context';
// Sync liveness rides a separate context so the badge that reads it doesn't
// re-render every screen — see store/sync.ts.
export { useSync } from './sync';
export type { StoreValue, ReplanWhere } from './types';
export type { SyncPhase, SyncStatus } from './sync';

export function StoreProvider({
  children,
  /** rendered until the cache (or the first pull) has painted — the front
     end's placeholder, see ApiStoreProvider */
  fallback,
}: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  // WriteGuard sits INSIDE the store provider and re-provides the same context
  // with mutations blocked for a lapsed account (store/write-guard.tsx). Placed
  // here rather than in `api.tsx` so the data layer never learns that
  // entitlement exists, and so screens keep calling `useStore()` unchanged.
  return (
    <ApiStoreProvider fallback={fallback}>
      <WriteGuard>{children}</WriteGuard>
    </ApiStoreProvider>
  );
}
